import "@tento-ui/component-builtin";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { ArrowDownIcon, LonaIcons } from "@tento-ui/ui/icons";

const SUGGESTIONS = [
  {
    id: "daily-habits",
    label: "Daily habits",
    prompt: "Help me track my daily habits like exercise and meditation",
  },
  {
    id: "food-journal",
    label: "Food journal",
    prompt: "Create a food journal to log my meals and nutrition",
  },
  {
    id: "task-management",
    label: "Task list",
    prompt: "Set up a task list to manage my weekly todos",
  },
];

@component({ name: "std-nux-sheet-card" })
export class NuxSheetCard extends LonaWebComponent {
  private $suggestions: HTMLElement = this.$("suggestions");
  private $composer: HTMLElement = this.$("composer");
  private $composerText: HTMLElement = this.$("composer-text");
  private $sendBtn: HTMLElement = this.$("send-btn");

  private selectedTemplateId: Option<string> = null;
  private onShowNullstate: Option<() => void> = null;
  private onSelectTemplate: Option<(templateId: string) => void> = null;
  private resizeObserver: Option<ResizeObserver> = null;

  static makeWith(callbacks: NuxSheetCard.Callbacks): NuxSheetCard {
    return NuxSheetCard.make().bind(callbacks);
  }

  bind(callbacks: NuxSheetCard.Callbacks): this {
    this.onShowNullstate = callbacks.onShowNullstate;
    this.onSelectTemplate = callbacks.onSelectTemplate;

    for (let i = 0; i < SUGGESTIONS.length; i++) {
      const suggestion = SUGGESTIONS[i];

      if (i > 0) {
        const $divider = document.createElement("div");
        $divider.classList.add("divider");
        this.$suggestions.appendChild($divider);
      }

      const $item = document.createElement("div");
      $item.classList.add("suggestion");
      $item.textContent = suggestion.label;
      GESTURE_MANAGER.addPointerEvent($item, {
        onClick: () => this.selectSuggestion(suggestion),
      });
      this.$suggestions.appendChild($item);
    }

    this.$sendBtn.appendChild(
      LonaIcons.make(ArrowDownIcon, { sizePx: 14, color: "currentColor" }),
    );
    GESTURE_MANAGER.addPointerEvent(this.$composer, {
      onClick: () => this.submitComposer(),
    });
    GESTURE_MANAGER.addPointerEvent(this.$sendBtn, {
      onClick: () => this.submitComposer(),
    });

    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(([entry]) => {
      const height = entry.contentRect.height || this.$composer.offsetHeight;
      this.$suggestions.style.transform = `translateY(-${height + 16 + 4 + 8}px)`;
    });
    this.resizeObserver.observe(this.$composer);

    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.resizeObserver) {
      this.resizeObserver.observe(this.$composer);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.resizeObserver?.disconnect();
  }

  private selectSuggestion(suggestion: (typeof SUGGESTIONS)[number]): void {
    this.selectedTemplateId = suggestion.id;
    this.$composerText.textContent = suggestion.prompt;
    this.$composer.classList.add("filled");
  }

  private submitComposer(): void {
    if (this.selectedTemplateId) {
      this.onSelectTemplate?.(this.selectedTemplateId);
    } else {
      this.onShowNullstate?.();
    }
  }

  static $styles = [
    css`
      :host {
        display: block;
        height: 100%;
      }

      #container {
        height: 100%;
        padding: 12px;
        justify-content: flex-end;
        position: relative;
      }

      #suggestions {
        position: absolute;
        bottom: 12px;
        left: 12px;
        right: 12px;
        flex-direction: column;
        align-items: stretch;
        gap: 0;
        transition: transform 0.3s ease;
      }

      .suggestion {
        color: var(--text-sec);
        font-size: var(--text-12px);
        padding: 8px 12px;
        cursor: pointer;
        transition: background 0.15s ease;
        border-radius: 8px;
        margin-block: 1px;
      }

      .suggestion:hover {
        background: var(--bg-hover);
        color: var(--text-pri);
      }

      .divider {
        height: 1px;
        background: var(--divider);
      }

      #composer {
        background: var(--bg-surface-light);
        border-radius: 12px;
        padding: 12px 12px;
        cursor: pointer;
        transition: background 0.15s ease;
      }

      #composer:hover {
        background: var(--bg-hover);
      }

      #composer-text {
        color: var(--text-ter);
      }

      #composer.filled #composer-text {
        color: var(--text-pri);
      }

      #action-bar {
        justify-content: flex-end;
        margin-top: 8px;
      }

      #send-btn {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: var(--text-ter);
        color: var(--bg);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transform: rotate(180deg);
        transition: background 0.15s ease;
      }

      #send-btn:hover {
        background: var(--text-sec);
      }

      #composer.filled #send-btn {
        background: var(--blue);
      }

      #send-btn {
        position: relative;
      }

      #send-btn::after {
        content: "";
        position: absolute;
        inset: 0;
        border-radius: inherit;
        background: var(--black12);
        opacity: 0;
        transition: opacity 0.15s ease;
        pointer-events: none;
      }

      #composer.filled #send-btn:hover::after {
        opacity: 1;
      }
    `,
  ];

  static $html = html`
    <std-col id="container">
      <std-col id="suggestions"></std-col>
      <std-col id="composer">
        <p id="composer-text" class=small>Create a sheet...</p>
        <std-row id="action-bar">
          <div id="send-btn"></div>
        </std-row>
      </std-col>
    </std-col>
  `;
}

export namespace NuxSheetCard {
  export type Callbacks = {
    onShowNullstate: () => void;
    onSelectTemplate: (templateId: string) => void;
  };
}
